// client/src/coms/workspace/useWorkspaceShortcuts.js
// Keyboard shortcuts for conversation tabs (all Alt+Shift, which Voxemera and browsers do not otherwise use):
//   Alt+Shift+→/←  next/previous tab     Alt+Shift+1…9  tab N       Alt+Shift+W  close tab
//   Alt+Shift+T    reopen closed tab     Alt+Shift+P    pin/unpin   Alt+Shift+S  toggle split
//   Alt+Shift+X    focus the other split pane
// On macOS, Option+Shift with a letter or digit types a character, so those are ignored while typing there.
import { useEffect, useRef } from 'react';
import { tabIdOf } from './workspaceModel';

function isMac() {
  try {
    return /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');
  } catch {
    return false;
  }
}

function isTyping(target) {
  const el = target;
  if (!el) return false;
  const tag = String(el.tagName || '').toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || !!el.isContentEditable;
}

export default function useWorkspaceShortcuts({ enabled, workspace, onActivate, onToggleSplit, onFocusOtherPane }) {
  const ref = useRef({ workspace, onActivate, onToggleSplit, onFocusOtherPane });
  ref.current = { workspace, onActivate, onToggleSplit, onFocusOtherPane };

  useEffect(() => {
    if (!enabled) return undefined;
    const mac = isMac();
    const onKey = (e) => {
      if (!e.altKey || !e.shiftKey || e.ctrlKey || e.metaKey) return;
      const { workspace: w, onActivate: activate, onToggleSplit: toggle, onFocusOtherPane: focusOther } = ref.current;
      const tabs = w.ws.tabs;
      const code = String(e.code || '');
      const isArrow = code === 'ArrowRight' || code === 'ArrowLeft';
      if (!isArrow && mac && isTyping(e.target)) return;
      const activeIdx = tabs.findIndex((t) => tabIdOf(t) === w.ws.active);
      let handled = true;
      if (isArrow) {
        if (!tabs.length) return;
        const d = code === 'ArrowRight' ? 1 : -1;
        const next = tabs[(Math.max(0, activeIdx) + d + tabs.length) % tabs.length];
        activate(tabIdOf(next));
      } else if (/^Digit[1-9]$/.test(code)) {
        const n = Number(code.slice(5)) - 1;
        const t = n === 8 ? tabs[tabs.length - 1] : tabs[n];
        if (t) activate(tabIdOf(t));
      } else if (code === 'KeyW') {
        const t = tabs[activeIdx];
        if (t && !t.p) w.close(tabIdOf(t));
      } else if (code === 'KeyT') {
        w.reopenClosed();
      } else if (code === 'KeyP') {
        const t = tabs[activeIdx];
        if (t) w.pin(tabIdOf(t), !t.p);
      } else if (code === 'KeyS') {
        toggle();
      } else if (code === 'KeyX') {
        focusOther();
      } else handled = false;
      if (handled) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);
}
